import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { posts } from '@/lib/blog';

// Resolve blog post slugs (e.g. /blog/oracle-webcenter-...) to their title so the
// breadcrumb reads cleanly instead of showing the raw slug.
const blogTitleBySlug: Record<string, string> = Object.fromEntries(
  posts.map((p) => [p.slug, p.title]),
);

const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbNameMap: { [key: string]: string } = {
    '': 'Home',
    'integrations': 'ERP Integrations',
    'technology': 'Platform',
    'ai-email-prevention': 'AI Email Prevention',
    'native-erp': 'Native ERP Integration',
    'multi-platform': 'Multi-Platform Hub',
    'industries': 'Industries',
    'healthcare': 'Healthcare',
    'education': 'Education',
    'manufacturing': 'Manufacturing',
    'property-management': 'Property Management',
    'oracle': 'Oracle',
    'netsuite': 'NetSuite',
    'sap': 'SAP',
    'dynamics': 'Dynamics 365',
    'yardi': 'Yardi Voyager',
    'customers': 'Customer Stories',
    'hacov': 'HACOV',
    'health-system-oracle-fusion': 'Health System on Oracle Fusion',
    'school-district-oracle-ebs': 'School District on Oracle EBS',
    'solutions': 'Solutions',
    'healthcare-oracle-fusion': 'Healthcare on Oracle Fusion',
    'education-oracle-ebs': 'Education on Oracle EBS',
    'conferences': 'Conferences',
    'plans': 'Plans',
    'blog': 'Blog',
    'resources': 'Resources',
    'roi-calculator': 'ROI Calculator',
    'privacy': 'Privacy Policy',
    'accessibility': 'Accessibility',
    'about': 'About',
  };

  // Pages that don't have actual routes (should not be clickable)
  const nonClickablePages = ['technology', 'industries', 'resources', 'solutions'];

  // Add breadcrumb schema
  useEffect(() => {
    if (pathnames.length === 0) return;

    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://ezcloud.ai/"
        },
        ...pathnames.map((pathname, index) => ({
          "@type": "ListItem",
          "position": index + 2,
          "name": breadcrumbNameMap[pathname] || blogTitleBySlug[pathname] || pathname,
          "item": `https://ezcloud.ai/${pathnames.slice(0, index + 1).join('/')}`
        }))
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(breadcrumbList);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [pathnames]);

  if (pathnames.length === 0) return null;

  return (
    <nav className="bg-gray-50 py-3 border-b" aria-label="Breadcrumb">
      <div className="container mx-auto px-4">
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <li>
            <Link 
              to="/" 
              className="flex items-center text-muted-foreground hover:text-primary transition-colors"
              aria-label="Go to homepage"
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Link>
          </li>
          
          {pathnames.map((pathname, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const displayName = breadcrumbNameMap[pathname] || blogTitleBySlug[pathname] || pathname;
            const isNonClickable = nonClickablePages.includes(pathname);

            return (
              <li key={pathname} className="flex items-center min-w-0">
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground flex-shrink-0" />
                {isLast ? (
                  <span className="text-foreground font-medium max-w-[60vw] sm:max-w-lg truncate inline-block align-bottom" aria-current="page">
                    {displayName}
                  </span>
                ) : isNonClickable ? (
                  <span className="text-muted-foreground">
                    {displayName}
                  </span>
                ) : (
                  <Link 
                    to={routeTo} 
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {displayName}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;